import { Link } from "react-router-dom";
import { ArrowRight, Heart, ShieldCheck, Sparkles, Quote,
  CloudRain, Hourglass, Palette, Home as HomeIcon, Lightbulb, ShieldPlus } from "lucide-react";
import { SERVICES, TESTIMONIALS, NEEDS } from "@/lib/data";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_lea-parentalite/artifacts/5vpgw1h2_19%20juil.%202026%2C%2020_45_47.png";
const ABOUT_IMG = "https://images.pexels.com/photos/8690656/pexels-photo-8690656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const NEED_ICONS = {
  emotions: CloudRain,
  tensions: Hourglass,
  eveil: Palette,
  cap: HomeIcon,
  comprendre: Lightbulb,
  prevenir: ShieldPlus,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden -mt-20 pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Une maman et son enfant en pleine nature au coucher du soleil" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#4A403A]/60 via-[#4A403A]/25 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="max-w-2xl">
            <p data-testid="hero-eyebrow" className="fade-up text-xs tracking-[0.25em] uppercase text-[#F4EBE6] mb-6">
              Éducatrice de Jeunes Enfants · Dijon
            </p>
            <h1 data-testid="hero-title" className="fade-up fade-up-delay-1 font-serif text-4xl md:text-5xl lg:text-6xl text-[#FAF7F2] leading-[1.08]">
              Conseil parental à domicile à Dijon,<br/>
              <em className="text-[#F4EBE6]">par l'expertise d'une éducatrice de jeunes enfants</em>.
            </h1>
            <p data-testid="hero-subtitle" className="fade-up fade-up-delay-2 mt-8 text-lg md:text-xl text-[#FAF7F2]/90 leading-relaxed max-w-xl">
              Coach parentale libérale et EJE diplômée d'État, je vous accompagne
              à domicile pour les familles avec enfants de 0 à 6 ans. Écoute, observation
              et outils concrets — à votre rythme.
            </p>
            <div className="fade-up fade-up-delay-3 mt-10 flex flex-wrap gap-4">
              <Link
                to="/rendez-vous"
                data-testid="hero-cta-booking"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#D2A69A] text-[#4A403A] text-sm tracking-wide font-medium transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAF7F2]"
              >
                Prendre rendez-vous <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 border border-[#FAF7F2]/40 text-[#FAF7F2] text-sm tracking-wide transition-all duration-300 hover:bg-[#FAF7F2]/10"
              >
                Découvrir les accompagnements
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Où en êtes-vous ? */}
      <section id="ou-en-etes-vous" data-testid="needs-section" className="bg-[#F4EBE6]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl mb-14">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-5">Vous êtes ici</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A403A] leading-[1.05]">
              Où en êtes-vous&nbsp;?
            </h2>
            <p className="mt-6 text-lg text-[#8B7E74] leading-relaxed">
              Chaque famille vit des moments différents. Peut-être vous reconnaissez-vous
              dans l'une de ces situations — c'est à ce moment-là que je peux vous accompagner.
            </p>
          </div>

          {/* 3-column cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEEDS.map((n) => {
              const Icon = NEED_ICONS[n.key];
              return (
                <article
                  key={n.key}
                  id={`need-${n.key}`}
                  data-testid={`need-card-${n.key}`}
                  className="bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer transition-all duration-300 hover:-translate-y-1 hover:shadow-soft scroll-mt-28"
                >
                  <span className="inline-flex w-14 h-14 rounded-2xl bg-[#F4EBE6] items-center justify-center text-[#BA8E82] mb-6">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-2xl text-[#4A403A] mb-3 leading-tight">{n.title}</h3>
                  <p className="text-[#8B7E74] leading-relaxed text-[15px]">{n.text}</p>
                </article>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/services"
              data-testid="needs-cta-services"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3a322d]"
            >
              Voir les accompagnements adaptés <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Heart, title: "Bienveillance", text: "Une écoute sans jugement, à votre rythme, dans le respect de votre histoire familiale." },
            { icon: ShieldCheck, title: "Expertise EJE", text: "Diplômée d'État d'Éducatrice de Jeunes Enfants, avec une approche fondée sur la psychologie et l'expérience de terrain." },
            { icon: Sparkles, title: "Outils concrets", text: "Vous repartez avec des repères, des routines et un plan d'actions adaptés à votre quotidien." },
          ].map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-card-${i}`}
              className="bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer transition-all duration-300 hover:-translate-y-1 hover:shadow-soft"
            >
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EBE6] items-center justify-center text-[#BA8E82] mb-6">
                <p.icon size={20} strokeWidth={1.6} />
              </span>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-3">{p.title}</h3>
              <p className="text-[#8B7E74] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Présentation Léa */}
      <section className="bg-[#F4EBE6]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-soft aspect-[4/5]">
              <img src={ABOUT_IMG} alt="Atelier d'éveil avec jouets en bois" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-5">À propos</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight mb-7">
              Une professionnelle,<br/>
              <em className="text-[#BA8E82]">une présence</em>.
            </h2>
            <p className="text-lg text-[#8B7E74] leading-relaxed mb-5">
              Je suis Léa, Éducatrice de Jeunes Enfants diplômée d'État. Depuis 5 ans,
              j'accompagne les enfants et leurs familles — d'abord en crèche puis en IME
              (Institut Médico-Éducatif), aujourd'hui à votre domicile à Dijon
              et ses agglomérations.
            </p>
            <p className="text-lg text-[#8B7E74] leading-relaxed mb-5">
              Mon approche : observer, écouter, comprendre — puis poser ensemble des gestes simples
              et soutenants pour la vie quotidienne avec votre enfant.
            </p>
            <p className="text-[15px] text-[#4A403A]/80 italic border-l-2 border-[#D2A69A] pl-4 mb-8">
              Concernée moi-même par le HPI et le TDAH, je porte une sensibilité particulière
              aux enfants et familles qui vivent avec ces profils — une formation dédiée est en préparation.
            </p>
            <Link
              to="/a-propos"
              data-testid="home-about-link"
              className="inline-flex items-center gap-2 text-[#4A403A] link-underline"
            >
              Découvrir mon parcours <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">Accompagnements</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight max-w-2xl">
              Six prestations pensées<br/>pour chaque étape.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="home-services-link"
            className="text-sm text-[#4A403A] link-underline"
          >
            Voir toutes les prestations →
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(0, 3).map((s) => (
            <Link
              key={s.slug}
              to="/services"
              data-testid={`home-service-card-${s.slug}`}
              className="group bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer transition-all duration-300 hover:-translate-y-1 hover:shadow-soft"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="text-xs tracking-[0.18em] uppercase text-[#BA8E82] bg-[#F4EBE6] rounded-full px-3 py-1">
                  {s.badge}
                </span>
                <span className="font-serif text-3xl text-[#4A403A]">{s.price}</span>
              </div>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-3">{s.title}</h3>
              <p className="text-[#8B7E74] leading-relaxed mb-6">{s.short}</p>
              <span className="text-sm text-[#4A403A] inline-flex items-center gap-2 link-underline">
                En savoir plus <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Témoignages */}
      <section className="bg-[#F4EBE6]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">Témoignages</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight">
              Les mots des familles<br/>accompagnées.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                data-testid={`testimonial-${i}`}
                className="bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer"
              >
                <Quote size={20} className="text-[#D2A69A] mb-5" />
                <p className="text-[#4A403A] leading-relaxed mb-6 font-serif text-xl italic">
                  « {t.text} »
                </p>
                <p className="text-sm text-[#4A403A] font-medium">{t.name}</p>
                <p className="text-xs text-[#8B7E74]">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
